import {ReactElement, useMemo} from 'react';

import {AdditionalInfo} from '../../../../../layout/additional-info/additional-info';
import {
    useAvailableCatalogs,
    useCatalogConfigForCompanyForm,
    useCatalogInfo,
} from '../../../../../provider/catalogs/catalogs-hook';
import {Locale} from '../../../../../provider/locale/localization';
import {LangKeyType} from '../../../../../provider/locale/translation/type';
import {ProvidersIdsEnum} from '../../../../../util/type';

import * as styles from './info-for-image.scss';

type PropsType = {
    label: LangKeyType;
    title: LangKeyType;
    minResolution: LangKeyType;
    maxResolution: LangKeyType;
    maxSize: LangKeyType;
    minSize: LangKeyType;
    supportedFormats?: LangKeyType;
    fileFormats: LangKeyType;
    additionalNode?: ReactElement | null;
    allowedCatalogIds?: Array<ProvidersIdsEnum>;
};

export function InfoForImage(props: PropsType): ReactElement {
    const {
        label,
        title,
        additionalNode,
        maxSize,
        maxResolution,
        minResolution,
        fileFormats,
        minSize,
        supportedFormats,
        allowedCatalogIds,
    } = props;

    const catalogConfigHook = useCatalogConfigForCompanyForm();
    const {getCatalogName} = useCatalogInfo();
    const {isCatalogAvailable} = useAvailableCatalogs();

    const filteredCatalogNames = useMemo(() => {
        if (!catalogConfigHook.result) {
            return null;
        }

        const defaultAllowedCatalogIds = [ProvidersIdsEnum.doubleGis, ProvidersIdsEnum.google, ProvidersIdsEnum.yandex];

        const catalogIdsToShow = allowedCatalogIds ?? defaultAllowedCatalogIds;

        const catalogs = catalogConfigHook.result
            .filter((catalog) => {
                const isAvailable = isCatalogAvailable(catalog.catalogId);
                const isAllowedCatalog = catalogIdsToShow.includes(catalog.catalogId);

                return isAvailable && isAllowedCatalog;
            })
            .map((catalog) => getCatalogName(catalog.catalogId))
            .filter((name) => name.length > 0);

        return catalogs.length > 0 ? catalogs.join(', ') : null;
    }, [catalogConfigHook.result, getCatalogName, isCatalogAvailable, allowedCatalogIds]);

    return (
        <AdditionalInfo
            className={styles.AdditionalInfo}
            leftNode={<Locale stringKey={label} />}
            title={<Locale stringKey={title} />}
        >
            <div>
                <span className={styles.AdditionalInfo_title}>
                    <Locale stringKey="COMPANY_FORM__GALLERY__HINT__MIN_RESOLUTION__TITLE" />
                </span>

                <Locale stringKey={minResolution} />
            </div>

            <div>
                <span className={styles.AdditionalInfo_title}>
                    <Locale stringKey="COMPANY_FORM__GALLERY__HINT__MAX_RESOLUTION__TITLE" />
                </span>

                <Locale stringKey={maxResolution} />
            </div>

            <div>
                <span className={styles.AdditionalInfo_title}>
                    <Locale stringKey="COMPANY_FORM__GALLERY__HINT__MIN_SIZE__TITLE" />
                </span>

                <Locale stringKey={minSize} />
            </div>

            <div>
                <span className={styles.AdditionalInfo_title}>
                    <Locale stringKey="COMPANY_FORM__GALLERY__HINT__MAX_SIZE__TITLE" />
                </span>

                <Locale stringKey={maxSize} />
            </div>

            {supportedFormats && (
                <div>
                    <span className={styles.AdditionalInfo_title}>
                        <Locale stringKey="COMPANY_FORM__GALLERY__HINT__FILE_SUPPORTED_FORMATS__TITLE" />
                    </span>

                    <Locale stringKey={supportedFormats} />
                </div>
            )}

            <div>
                <span className={styles.AdditionalInfo_title}>
                    <Locale stringKey="COMPANY_FORM__GALLERY__HINT__FILE_FORMATS__TITLE" />
                </span>

                <Locale stringKey={fileFormats} />
            </div>

            {filteredCatalogNames && (
                <div>
                    <span className={styles.AdditionalInfo_title}>
                        <Locale stringKey="COMPANY_FORM__IMAGE__SENT_TO_CATALOGS__TITLE" />
                    </span>

                    {filteredCatalogNames}
                </div>
            )}

            {additionalNode && <div>{additionalNode}</div>}
        </AdditionalInfo>
    );
}
